import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ColumnLineChart from "../components/dashboard/ColumnLineChart";
import ingredientApi from "../api/ingredientApi";
import { getServingGramWeight } from "../utils/servingUnit";

const defaultTotals = {
  totalCost: 0,
  totalRecipes: 0,
  totalIngredients: 0,
};

const defaultInsights = {
  topRecipes: [],
  avgCostPerRecipe: 0,
  avgIngredientsPerRecipe: 0,
  costPerIngredient: 0,
  coveragePct: 0,
  uniqueIngredients: 0,
  costedLines: 0,
  recipesWithSpend: 0,
};

const AppHome = () => {
  const user = useSelector((state) => state.user.value);
  const [ingredientsApiDetails, setIngredientsApiDetails] = useState([]);
  const [totals, setTotals] = useState(defaultTotals);
  const [insights, setInsights] = useState(defaultInsights);
  const [loadingSummary, setLoadingSummary] = useState(true);

  useEffect(() => {
    if (!user?.id) return;

    let active = true;
    const fetchDashboardData = async () => {
      setLoadingSummary(true);
      try {
        const recipesIngredientsArray = await ingredientApi.getCosts(user.id);
        const recipesIngredients = recipesIngredientsArray?.[1] || [];
        const summary = buildInsights(recipesIngredients);
        const apiRecipeCount =
          recipesIngredientsArray?.[2]?.numberOfRecipes || summary.recipeCount;

        const avgCostPerRecipe =
          apiRecipeCount > 0 ? summary.totalCost / apiRecipeCount : 0;
        const avgIngredientsPerRecipe =
          apiRecipeCount > 0
            ? recipesIngredients.length / apiRecipeCount
            : 0;
        const costPerIngredient =
          recipesIngredients.length > 0
            ? summary.totalCost / recipesIngredients.length
            : 0;

        if (!active) return;
        setIngredientsApiDetails(recipesIngredients);
        setTotals({
          totalCost: summary.totalCost,
          totalRecipes: apiRecipeCount,
          totalIngredients: recipesIngredients.length,
        });
        setInsights({
          topRecipes: summary.topRecipes,
          avgCostPerRecipe,
          avgIngredientsPerRecipe,
          costPerIngredient,
          coveragePct: summary.coveragePct,
          uniqueIngredients: summary.uniqueIngredients,
          costedLines: summary.costedLines,
          recipesWithSpend: summary.recipesWithSpend,
        });
      } catch (err) {
        console.log(err);
        if (!active) return;
        setIngredientsApiDetails([]);
        setTotals(defaultTotals);
        setInsights(defaultInsights);
      } finally {
        if (active) {
          setLoadingSummary(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      active = false;
    };
  }, [user]);

  const statCards = useMemo(
    () => [
      {
        label: "Production spend",
        value: formatCurrency(totals.totalCost),
        helper: `${formatNumber(insights.costedLines)} priced ingredient lines`,
        chipLabel: `${insights.coveragePct.toFixed(0)}% coverage`,
        chipColor: "success",
      },
      {
        label: "Recipes live",
        value: formatNumber(totals.totalRecipes),
        helper: "Active across all boards",
        chipLabel: `${insights.avgIngredientsPerRecipe.toFixed(
          1
        )} avg ingredients`,
        chipColor: "primary",
      },
      {
        label: "Ingredient lines",
        value: formatNumber(totals.totalIngredients),
        helper: `${formatNumber(
          insights.uniqueIngredients || totals.totalIngredients
        )} unique ingredients`,
        chipLabel: `${formatCurrency(insights.costPerIngredient)}/line`,
        chipColor: "secondary",
      },
      {
        label: "Avg cost / recipe",
        value: formatCurrency(insights.avgCostPerRecipe),
        helper: "Based on recent ingredient spend",
        chipLabel: null,
      },
    ],
    [
      totals.totalCost,
      totals.totalRecipes,
      totals.totalIngredients,
      insights.costedLines,
      insights.coveragePct,
      insights.avgIngredientsPerRecipe,
      insights.uniqueIngredients,
      insights.costPerIngredient,
      insights.avgCostPerRecipe,
    ]
  );

  return (
    <Box
      sx={{
        py: { xs: 2, md: 3 },
      }}
    >
      <Stack spacing={3}>
        <Paper
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            background:
              "linear-gradient(135deg, #0f172a 0%, #1d283a 45%, #111827 100%)",
            color: "#fff",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background:
                "radial-gradient(circle at 80% 20%, rgba(59,130,246,0.25), transparent 45%)",
            }}
          />
          <Stack spacing={2} sx={{ position: "relative" }}>
            <Typography variant="h5" fontWeight={600}>
              Hey {user?.username || "Creator"}, your kitchen pulse is ready 🔥
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.8)", maxWidth: 620 }}
            >
              Track production spend, ingredient coverage, and recipe velocity
              without digging through multiple tabs.
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              flexWrap="wrap"
            >
              <Chip
                label={`${formatNumber(totals.totalRecipes)} live recipes`}
                sx={{ color: "#0f172a", backgroundColor: "#fff" }}
              />
              <Chip
                label={`${formatNumber(
                  totals.totalIngredients
                )} ingredient lines`}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.5)",
                  borderWidth: 1,
                  borderStyle: "solid",
                }}
                variant="outlined"
              />
              <Chip
                label={`${insights.coveragePct.toFixed(0)}% cost coverage`}
                sx={{
                  color: "#0f172a",
                  backgroundColor: "rgba(16,185,129,0.9)",
                  fontWeight: 600,
                }}
              />
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={2}>
          {statCards.map((stat) => (
            <Grid key={stat.label} item xs={12} sm={6} lg={3}>
              <StatCard {...stat} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
                mb={2}
              >
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Cost & ingredient velocity
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Visualize how ingredient spend trends with recipe activity.
                  </Typography>
                </Box>
                {loadingSummary && (
                  <Chip size="small" label="Refreshing..." color="primary" />
                )}
              </Stack>
              <Box sx={{ flexGrow: 1, minHeight: 320 }}>
                <ColumnLineChart recipesDetails={ingredientsApiDetails} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Top recipes by spend
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Highest costing formulas based on ingredient totals.
                </Typography>
              </Box>
              {insights.topRecipes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Add ingredient costs to your recipes to unlock ranked
                  insights.
                </Typography>
              ) : (
                <Stack
                  spacing={1.5}
                  divider={
                    <Divider
                      flexItem
                      sx={{ borderColor: "rgba(148,163,184,0.3)" }}
                    />
                  }
                >
                  {insights.topRecipes.map((recipe, index) => (
                    <Box
                      key={`${recipe.name}-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {recipe.name || `Recipe ${index + 1}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {recipe.ingredients} ingredients
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end">
                        <Typography variant="subtitle1" fontWeight={600}>
                          {formatCurrency(recipe.cost)}
                        </Typography>
                        <Chip
                          size="small"
                          label={`#${index + 1}`}
                          color={index === 0 ? "primary" : "default"}
                        />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Data coverage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {insights.coveragePct.toFixed(0)}% of ingredient rows include
                  pricing data.
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(insights.coveragePct, 100)}
                sx={{ height: 10, borderRadius: 999 }}
              />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                divider={
                  <Divider
                    flexItem
                    orientation="vertical"
                    sx={{ display: { xs: "none", sm: "block" } }}
                  />
                }
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {formatNumber(insights.costedLines)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    costed lines
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {formatNumber(
                      Math.max(
                        totals.totalIngredients - insights.costedLines,
                        0
                      )
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    awaiting cost
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                height: "100%",
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Operational snapshot
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Quick ratios to keep your production pipeline balanced.
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <SnapshotMetric
                      label="Avg ingredients / recipe"
                      value={formatNumber(insights.avgIngredientsPerRecipe)}
                      helper="Across all tracked recipes"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SnapshotMetric
                      label="Cost per ingredient"
                      value={formatCurrency(insights.costPerIngredient)}
                      helper="Normalized per ingredient line"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SnapshotMetric
                      label="Unique ingredients"
                      value={formatNumber(
                        insights.uniqueIngredients || totals.totalIngredients
                      )}
                      helper="Distinct raw materials tracked"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SnapshotMetric
                      label="Recipes with spend data"
                      value={formatNumber(insights.recipesWithSpend)}
                      helper="Eligible for deep-dive ranking"
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

const StatCard = ({ label, value, helper, chipLabel, chipColor = "primary" }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 3,
      p: 3,
      height: "100%",
      border: "1px solid",
      borderColor: "rgba(15,23,42,0.08)",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)",
    }}
  >
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={600}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {helper}
      </Typography>
      {chipLabel && (
        <Chip
          label={chipLabel}
          size="small"
          color={chipColor}
          sx={{ alignSelf: "flex-start" }}
        />
      )}
    </Stack>
  </Paper>
);

const SnapshotMetric = ({ label, value, helper }) => (
  <Box
    sx={{
      borderRadius: 2,
      p: 2,
      border: "1px solid",
      borderColor: "rgba(15,23,42,0.08)",
      height: "100%",
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={600}>
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {helper}
    </Typography>
  </Box>
);

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: value > 1000 ? 0 : 2,
  }).format(value || 0);

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 1,
  }).format(value || 0);

const buildInsights = (rows = []) => {
  let totalCost = 0;
  let costedLines = 0;
  const recipeMap = new Map();
  const uniqueIngredients = new Set();
  const recipesWithSpend = new Set();

  rows.forEach((item, index) => {
    const { key, displayName } = resolveRecipeIdentifiers(item, index);

    if (!recipeMap.has(key)) {
      recipeMap.set(key, { cost: 0, ingredients: 0, name: displayName });
    }

    const ingredientCost = computeIngredientCost(item);
    const stats = recipeMap.get(key);
    stats.ingredients += 1;
    if (!stats.name && displayName) {
      stats.name = displayName;
    }
    if (ingredientCost > 0) {
      stats.cost += ingredientCost;
      totalCost += ingredientCost;
      costedLines += 1;
      recipesWithSpend.add(key);
    }

    const ingredientIdentifier =
      item?.ingredient ||
      item?.ingredient_name ||
      item?.ingredientId ||
      item?.ingredientID ||
      item?.name;
    if (ingredientIdentifier) {
      uniqueIngredients.add(ingredientIdentifier);
    }
  });

  const topRecipes = Array.from(recipeMap.values())
    .map((stats, index) => ({
      name: stats.name || `Recipe ${index + 1}`,
      cost: stats.cost,
      ingredients: stats.ingredients,
    }))
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 5);

  const coveragePct = rows.length ? (costedLines / rows.length) * 100 : 0;

  return {
    totalCost,
    recipeCount: recipeMap.size,
    uniqueIngredients: uniqueIngredients.size || rows.length,
    costedLines,
    coveragePct,
    topRecipes,
    recipesWithSpend: recipesWithSpend.size,
  };
};

const computeIngredientCost = (item = {}) => {
  const rawYield = parseFloat(item.cost_yield);
  const costYield = !rawYield || rawYield === 0 ? 100 : rawYield;
  const cost = parseFloat(item.cost);
  const costPerGram = parseFloat(item.cost_per_gram);
  const qty = parseFloat(item.qty);
  const unitWeight = getServingGramWeight(item.serving_unit);

  if (
    !isFinite(cost) ||
    cost === 0 ||
    !isFinite(costPerGram) ||
    costPerGram === 0 ||
    !isFinite(qty) ||
    qty === 0 ||
    !isFinite(unitWeight) ||
    unitWeight === 0
  ) {
    return 0;
  }

  const derived =
    (cost / costPerGram) * (100 / costYield) * unitWeight * qty;
  return Number.isFinite(derived) ? derived : 0;
};

const resolveRecipeIdentifiers = (item = {}, index = 0) => {
  const recipeString =
    typeof item?.recipe === "string" && !isLikelyId(item.recipe)
      ? item.recipe.trim()
      : null;
  const recipeIdString =
    typeof item?.recipeId === "string" && !isLikelyId(item.recipeId)
      ? item.recipeId.trim()
      : null;

  const candidateName =
    item?.recipe_title ||
    item?.recipeTitle ||
    item?.recipe_label ||
    item?.recipeLabel ||
    item?.recipeName ||
    item?.recipe_name ||
    item?.title ||
    item?.name ||
    (typeof item?.recipe === "object" && item.recipe?.title) ||
    recipeString ||
    recipeIdString;

  const displayName = candidateName || `Recipe ${index + 1}`;

  const key =
    item?.recipe_id ??
    item?.recipeId ??
    (typeof item?.recipe === "object"
      ? item.recipe?.id
      : item?.recipe) ??
    displayName ??
    `recipe-${index}`;

  return { key: key ?? `recipe-${index}`, displayName };
};

const isLikelyId = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "number") return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return /^\d+$/.test(trimmed) || /^[a-f0-9]{24}$/i.test(trimmed);
  }
  return false;
};

export default AppHome;
